import {
  Box,
  Flex,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import {Link, NavLink, useLocation} from 'react-router-dom';
import {CaretDown, CaretUp} from '@carbon/icons-react';
import {useIsFreeOrListBuilderCustomer} from '../../hooks/api/metadata';
import {CreditUsage, CreditPurchase} from './CreditUsage';
import {AppMenu} from './AppMenu';

const baseStyle = {
  borderRadius: 'md',
  cursor: 'pointer',
  color: 'kgray.50',
  fontWeight: 'normal',
  paddingX: 3,
  paddingY: 2,
  _hover: {
    bg: 'kgray.500',
  },
};

const activeStyle = {
  ...baseStyle,
  bg: 'kgray.500',
  fontWeight: 'medium',
};

const NavItem = ({to, children}: {to: string; children: React.ReactNode}) => {
  return (
    <NavLink
      to={to}
      className={({isActive}) => (isActive ? 'activeNavLink' : undefined)}
    >
      <Flex
        direction="column"
        justify="space-around"
        {...baseStyle}
        sx={{'.activeNavLink &': activeStyle}}
      >
        {children}
      </Flex>
    </NavLink>
  );
};

const NavDropdown = ({
  label,
  items,
}: {
  label: string;
  items: {label: string; path: string}[];
}) => {
  const location = useLocation();
  const isActive = items.some((item) =>
    location.pathname.startsWith(item.path)
  );
  const {isOpen, onOpen, onClose} = useDisclosure();

  return (
    <Menu
      isLazy
      placement="bottom"
      autoSelect={false}
      gutter={0}
      isOpen={isOpen}
    >
      <MenuButton
        sx={isActive ? activeStyle : undefined}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        {...baseStyle}
      >
        <Flex align="center" gap="2">
          {label}
          {isOpen ? <CaretUp /> : <CaretDown />}
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList
          bg="kgray.400"
          border="none"
          borderTopRadius="0"
          borderBottomRadius="lg"
          minW="auto"
          p="2"
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
        >
          {items.map((item) => (
            <MenuItem
              key={item.path}
              as={Link}
              to={item.path}
              bg="transparent"
              color="kgray.50"
              _hover={{
                textDecoration: 'underline',
                textUnderlineOffset: '6px',
              }}
              px="4"
              py="3"
              onClick={onClose}
            >
              {item.label}
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export const Header = ({token}: {token: string}) => {
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();

  return (
    <Box position="fixed" top={0} left={0} right={0} height="50px" zIndex={5}>
      <Flex
        bg="kgray.400"
        color="white"
        align="center"
        h="50"
        gap={2}
        px={4}
        justifyContent="space-between"
      >
        <Box as={Link} flexShrink="0" to="/discover/recommended">
          <Image
            display={{base: 'block', md: 'none'}}
            height="30px"
            src="/Keyplay_Primary_Icon.svg"
          />
          <Image
            display={{base: 'none', md: 'block'}}
            height="30px"
            src="/Keyplay_Primary_Logo_WhiteText.svg"
          />
        </Box>
        <HStack flexShrink="0" ml={{base: 0, md: 20}}>
          <NavItem to="/discover">Discover</NavItem>
          <NavDropdown
            label="Accounts"
            items={[
              {label: 'My Accounts', path: '/accounts'},
              {label: 'Account Lists', path: '/accounts/lists'},
            ]}
          />
          {!isFreeOrListBuilderCustomer && (
            <NavItem to="/agents">Agents</NavItem>
          )}
        </HStack>
        <Flex justifyContent="end" gap="6">
          {isFreeOrListBuilderCustomer && (
            <CreditPurchase>
              <CreditUsage alignContent={'center'} height="32px" />
            </CreditPurchase>
          )}
          <AppMenu token={token} />
        </Flex>
      </Flex>
    </Box>
  );
};
